import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { DELIVERY_SLOTS, LANGUAGES, ITEMS, DAY_NAMES, getStatusBadgeClass } from '../lib/constants';
import { Plus, Search, Edit2, Trash2, X, Phone, ChevronDown, IndianRupee } from 'lucide-react';

export default function SubscribersPage() {
  const [subs, setSubs] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [plans, setPlans] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterApt, setFilterApt] = useState('All');

  const defaultForm = {
    name: '', mobile: '', apt: '', area: '', flat: '', slot: '6:00-6:30 AM',
    start: '', planId: '', planName: '', planPrice: 1499, planDuration: 'monthly',
    deliveryDays: [0, 1, 2, 3, 4], status: 'active', daysLeft: 22, totalDeliveries: 22,
    quantity: 1, discount: 0, discountType: 'flat', payableAmount: 0,
    items: [], bottle: 'no', langs: [], remarks: '', deliveryBoyId: ''
  };
  const [form, setForm] = useState(defaultForm);

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, boysRes, plansRes, aptsRes] = await Promise.all([
        api.getSubscribers({ status: filterStatus !== 'All' ? filterStatus : undefined, apt: filterApt !== 'All' ? filterApt : undefined, search: search || undefined }),
        api.getDeliveryBoys(),
        api.getPlans(),
        api.getApartments()
      ]);
      setSubs(subsRes.data);
      setDeliveryBoys(boysRes.data);
      setPlans(plansRes.data);
      setApartments(aptsRes.data);
    } catch (err) { console.error('Fetch error:', err.message); }
    setLoading(false);
  }, [filterStatus, filterApt, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calculate payable amount when form changes
  useEffect(() => {
    const basePrice = form.planPrice * form.quantity;
    let discountAmt = 0;
    if (form.discountType === 'percent') {
      discountAmt = basePrice * (form.discount / 100);
    } else {
      discountAmt = form.discount;
    }
    const payable = Math.max(0, basePrice - discountAmt);
    setForm(f => ({ ...f, payableAmount: Math.round(payable) }));
  }, [form.planPrice, form.quantity, form.discount, form.discountType]);

  const openNew = () => {
    const defaultPlan = plans.find(p => p.price === 1499) || plans[0];
    setForm({
      ...defaultForm,
      start: new Date().toISOString().split('T')[0],
      planId: defaultPlan?.id || '',
      planName: defaultPlan?.name || '',
      planPrice: defaultPlan?.price || 1499,
      planDuration: defaultPlan?.duration || 'monthly',
      deliveryDays: defaultPlan?.deliveryDays || [0, 1, 2, 3, 4],
      daysLeft: defaultPlan?.totalDeliveries || 22,
      totalDeliveries: defaultPlan?.totalDeliveries || 22,
      payableAmount: defaultPlan?.price || 1499
    });
    setEditSub(null);
    setShowModal(true);
  };

  const openEdit = (sub) => {
    setForm({
      ...defaultForm,
      ...sub,
      items: sub.items || [],
      langs: sub.langs || [],
      quantity: sub.quantity || 1,
      discount: sub.discount || 0,
      discountType: sub.discountType || 'flat',
      deliveryDays: sub.deliveryDays || [0, 1, 2, 3, 4],
      planPrice: sub.planPrice || sub.plan || 1499,
      planName: sub.planName || '',
      planId: sub.planId || '',
      planDuration: sub.planDuration || 'monthly',
      totalDeliveries: sub.totalDeliveries || 22,
      payableAmount: sub.payableAmount || 0
    });
    setEditSub(sub);
    setShowModal(true);
  };

  const handlePlanChange = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (plan) {
      setForm(f => ({
        ...f,
        planId: plan.id,
        planName: plan.name,
        planPrice: plan.price,
        planDuration: plan.duration,
        deliveryDays: plan.deliveryDays,
        daysLeft: plan.totalDeliveries,
        totalDeliveries: plan.totalDeliveries
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSub) {
        await api.updateSubscriber(editSub.id, form);
      } else {
        await api.createSubscriber(form);
      }
      setShowModal(false);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    await api.deleteSubscriber(id);
    fetchData();
  };

  const toggleItem = (field, val) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(val) ? f[field].filter(v => v !== val) : [...f[field], val]
    }));
  };

  const getDeliveryBoyName = (id) => {
    const boy = deliveryBoys.find(b => b._id === id || b.id === id);
    return boy ? boy.name : '-';
  };

  const getPlanLabel = (sub) => {
    if (sub.planName) return sub.planName;
    const price = sub.planPrice || sub.plan || 0;
    return `${price.toLocaleString('en-IN')}`;
  };

  return (
    <div data-testid="subscribers-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Subscribers</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>{subs.length} subscribers</p>
        </div>
        <button data-testid="add-subscriber-button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Add Subscriber
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#78716C' }} />
          <input data-testid="subscriber-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or mobile..." className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }} />
        </div>
        <div className="relative">
          <select data-testid="subscriber-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            {['All', 'active', 'out-of-town', 'paused', 'inactive'].map(s => <option key={s} value={s}>{s === 'All' ? 'All Status' : s}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
        <div className="relative">
          <select data-testid="subscriber-filter-apt" value={filterApt} onChange={e => setFilterApt(e.target.value)} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            <option>All</option>
            {apartments.map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
        <div className="overflow-x-auto">
          <table className="w-full gm-table">
            <thead>
              <tr><th>Name</th><th>Mobile</th><th>Apartment / Flat</th><th>Plan</th><th>Qty</th><th>Payable</th><th>Status</th><th>Days Left</th><th>Slot</th><th>Delivery Boy</th><th></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="text-center py-8" style={{ color: '#78716C' }}>Loading...</td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-8" style={{ color: '#78716C' }}>No subscribers found.</td></tr>
              ) : subs.map((sub, i) => (
                <tr key={sub.id} className="fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <td style={{ color: '#F5F5F4' }} className="font-medium">{sub.name}</td>
                  <td><a href={`tel:${sub.mobile}`} className="inline-flex items-center gap-1" style={{ color: '#C8A96E' }}><Phone className="w-3 h-3" />{sub.mobile}</a></td>
                  <td>{sub.apt}{sub.flat ? ` / ${sub.flat}` : ''}</td>
                  <td className="text-xs">
                    <div style={{ color: '#F5F5F4' }}>{getPlanLabel(sub)}</div>
                    <div style={{ color: '#78716C' }}>{(sub.deliveryDays || [0,1,2,3,4]).map(d => DAY_NAMES[d]).join(', ')}</div>
                  </td>
                  <td className="text-center">{sub.quantity || 1}</td>
                  <td>
                    <span style={{ color: '#C8A96E' }}>
                      {(sub.payableAmount || (sub.planPrice || sub.plan || 0) * (sub.quantity || 1)).toLocaleString('en-IN')}
                    </span>
                    {sub.discount > 0 && <span className="ml-1 text-xs" style={{ color: '#4ADE80' }}>(-{sub.discount}{sub.discountType === 'percent' ? '%' : ''})</span>}
                  </td>
                  <td><span className={`${getStatusBadgeClass(sub.status)} px-2.5 py-0.5 rounded-full text-xs font-semibold`}>{sub.status}</span></td>
                  <td className="text-center">
                    <span style={{ color: sub.daysLeft <= 3 ? '#F87171' : sub.daysLeft <= 7 ? '#FBBF24' : '#F5F5F4' }}>
                      {sub.daysLeft}
                    </span>
                    <span className="text-xs" style={{ color: '#78716C' }}>/{sub.totalDeliveries || 22}</span>
                  </td>
                  <td className="text-xs whitespace-nowrap">{sub.slot}</td>
                  <td className="text-xs">{sub.deliveryBoyId ? getDeliveryBoyName(sub.deliveryBoyId) : '-'}</td>
                  <td>
                    <div className="flex gap-1">
                      <button data-testid={`edit-sub-${sub.id}`} onClick={() => openEdit(sub)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-sub-${sub.id}`} onClick={() => handleDelete(sub.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-2xl rounded-xl max-h-[85vh] overflow-y-auto" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editSub ? 'Edit Subscriber' : 'Add New Subscriber'}</h3>
              <button data-testid="close-sub-modal" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Full Name *</label>
                  <input data-testid="sub-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Mobile *</label>
                  <input data-testid="sub-mobile" required maxLength={10} value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Apartment</label>
                  <div className="relative">
                    <select data-testid="sub-apt" value={form.apt} onChange={e => setForm({...form, apt: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                      <option value="">-- select --</option>
                      {apartments.map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Flat No. *</label>
                  <input data-testid="sub-flat" required value={form.flat} onChange={e => setForm({...form, flat: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Area</label>
                  <input value={form.area} onChange={e => setForm({...form, area: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Delivery Slot</label>
                  <div className="relative">
                    <select value={form.slot} onChange={e => setForm({...form, slot: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                      {DELIVERY_SLOTS.map(s => <option key={s}>{s}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Subscription Start</label>
                  <input type="date" value={form.start} onChange={e => setForm({...form, start: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Status</label>
                  <div className="relative">
                    <select data-testid="sub-status" value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                  </div>
                </div>
              </div>

              {/* Subscription Plan Section */}
              <div className="rounded-lg p-4" style={{ background: '#100E0C', border: '1px solid rgba(200,169,110,0.2)' }}>
                <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>Subscription Plan</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Select Plan</label>
                    <div className="relative">
                      <select data-testid="sub-plan" value={form.planId} onChange={e => handlePlanChange(e.target.value)} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                        <option value="">-- choose a plan --</option>
                        {plans.filter(p => p.isActive !== false).map(p => (
                          <option key={p.id} value={p.id}>{p.name} - Rs.{p.price}/{p.duration === 'weekly' ? 'week' : 'month'} ({p.totalDeliveries} deliveries)</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                    </div>
                  </div>

                  {form.planId && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Delivery Days</label>
                        <div className="text-sm" style={{ color: '#F5F5F4' }}>
                          {form.deliveryDays.map(d => DAY_NAMES[d]).join(', ')}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Total Deliveries</label>
                        <div className="text-sm" style={{ color: '#F5F5F4' }}>{form.totalDeliveries}</div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Quantity (Subscriptions)</label>
                    <input data-testid="sub-quantity" type="number" min={1} max={5} value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 1})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Bottle Deposit</label>
                    <div className="relative">
                      <select value={form.bottle} onChange={e => setForm({...form, bottle: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                        <option value="yes">Paid</option>
                        <option value="no">Not paid</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                    </div>
                  </div>
                </div>

                {/* Discount & Payable */}
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid #2A241F' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Discount</label>
                      <input data-testid="sub-discount" type="number" min={0} value={form.discount} onChange={e => setForm({...form, discount: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Discount Type</label>
                      <div className="relative">
                        <select data-testid="sub-discount-type" value={form.discountType} onChange={e => setForm({...form, discountType: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                          <option value="flat">Flat (Rs.)</option>
                          <option value="percent">Percent (%)</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#4ADE80' }}>Payable Amount</label>
                      <div className="flex items-center gap-1 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.2)' }}>
                        <IndianRupee className="w-4 h-4" style={{ color: '#4ADE80' }} />
                        <span className="text-lg font-bold" style={{ color: '#4ADE80' }}>{form.payableAmount.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs" style={{ color: '#78716C' }}>
                    Base: Rs.{form.planPrice} x {form.quantity} = Rs.{(form.planPrice * form.quantity).toLocaleString('en-IN')}
                    {form.discount > 0 && ` | Discount: ${form.discountType === 'percent' ? form.discount + '%' : 'Rs.' + form.discount}`}
                  </div>
                </div>
              </div>

              {/* Delivery Boy */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Delivery Boy</label>
                <div className="relative">
                  <select data-testid="sub-delivery-boy" value={form.deliveryBoyId} onChange={e => setForm({...form, deliveryBoyId: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    <option value="">-- none --</option>
                    {deliveryBoys.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>

              {/* Items */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#C8A96E' }}>Items Subscribed</label>
                <div className="flex flex-wrap gap-2">
                  {ITEMS.map(item => (
                    <button key={item} type="button" onClick={() => toggleItem('items', item)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{ background: form.items.includes(item) ? 'rgba(200,169,110,0.2)' : '#100E0C', border: `1px solid ${form.items.includes(item) ? 'rgba(200,169,110,0.5)' : '#2A241F'}`, color: form.items.includes(item) ? '#C8A96E' : '#A8A29E' }}
                    >{item}</button>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#C8A96E' }}>Languages</label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map(l => (
                    <button key={l} type="button" onClick={() => toggleItem('langs', l)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{ background: form.langs.includes(l) ? 'rgba(200,169,110,0.2)' : '#100E0C', border: `1px solid ${form.langs.includes(l) ? 'rgba(200,169,110,0.5)' : '#2A241F'}`, color: form.langs.includes(l) ? '#C8A96E' : '#A8A29E' }}
                    >{l}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Remarks</label>
                <textarea value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>

              <div className="flex gap-3 pt-2">
                <button data-testid="save-subscriber-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
                  {editSub ? 'Update Subscriber' : 'Save Subscriber'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
